import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../driver-module/driver-panel.css";
import { DeliveryStatusValue, IDriver, IDriverActiveOrder } from "../models/IDriver";
import {
  getDriverActiveOrders,
  getMyDriverProfile,
  updateDeliveryStatus,
  updateDriverStatus,
} from "../services/driver";
import { useDriverLocationSharing } from "../driver-module/hooks/useDriverLocationSharing";
import ActiveOrderCard from "../driver-module/components/ActiveOrderCard";
import DeliveryHistory from "../driver-module/components/DeliveryHistory";
import DriverMapModal, { MapTarget } from "../driver-module/components/DriverMapModal";
import RoutePlannerModal from "../driver-module/components/RoutePlannerModal";
import ConfirmSheet from "../driver-module/components/ConfirmSheet";
import { collectsAtDoor, formatMoney, formatRelative, normalizeDriverStatus } from "../driver-module/utils";

const POLL_MS = 30000;

type Toast = { text: string; tone: "success" | "error" | "info" };
type ConfirmState = { kind: "deliver" | "cancel"; order: IDriverActiveOrder };

function extractError(err: any, fallback: string): string {
  const data = err?.response?.data;
  return (Array.isArray(data) ? data[0]?.description : data?.description) ?? fallback;
}

function initials(name?: string | null): string {
  return (name ?? "").split(" ").filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "K";
}

function DriverPanel() {
  const navigate = useNavigate();

  const [driver, setDriver] = useState<IDriver | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");

  const [orders, setOrders] = useState<IDriverActiveOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const [tab, setTab] = useState<"active" | "history">("active");
  const [busyDeliveryId, setBusyDeliveryId] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [mapTarget, setMapTarget] = useState<MapTarget | null>(null);
  const [showRoute, setShowRoute] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [now, setNow] = useState(Date.now());

  const knownIdsRef = useRef<Set<string> | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  const showToast = (text: string, tone: Toast["tone"]) => {
    setToast({ text, tone });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3500);
  };

  const refresh = async (silent: boolean) => {
    if (!driver) return;
    if (!silent) setRefreshing(true);
    try {
      const [profileRes, ordersRes] = await Promise.all([getMyDriverProfile(), getDriverActiveOrders(driver.id)]);
      const list = ordersRes.data ?? [];
      if (knownIdsRef.current && list.some((o) => !knownIdsRef.current!.has(o.deliveryId))) {
        showToast("Yeni sipariş atandı!", "info");
        navigator.vibrate?.([200, 100, 200]);
      }
      knownIdsRef.current = new Set(list.map((o) => o.deliveryId));
      setDriver(profileRes.data);
      setOrders(list);
      setOrdersError("");
    } catch {
      setOrdersError("Siparişler yüklenemedi. Bağlantını kontrol et.");
    } finally {
      setOrdersLoading(false);
      setRefreshing(false);
    }
  };

  // Interval ve event dinleyicileri her zaman en güncel refresh'i çağırsın diye.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    getMyDriverProfile()
      .then((res) => setDriver(res.data))
      .catch(() => setProfileError("Kurye profilin bulunamadı. Bu hesap bir kuryeye bağlı olmayabilir."))
      .finally(() => setProfileLoading(false));
  }, []);

  const driverId = driver?.id ?? null;
  useEffect(() => {
    if (!driverId) return;
    refreshRef.current(true);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refreshRef.current(true);
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshRef.current(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [driverId]);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 20000);
    return () => {
      window.clearInterval(t);
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const status = normalizeDriverStatus(driver?.status);
  const isOnline = status !== "Offline";
  const isEnabled = driver?.isEnabled ?? false;
  const onDeliveryLocked = status === "OnDelivery" && orders.length > 0;
  const sharing = useDriverLocationSharing(driverId, isEnabled && isOnline);

  const toggleOnline = async () => {
    if (!driver || statusUpdating) return;
    const next = isOnline ? "Offline" : "Available";
    if (next === "Offline" && orders.length > 0) {
      showToast("Elinde teslimat varken çevrimdışı olamazsın.", "error");
      return;
    }
    setStatusUpdating(true);
    try {
      await updateDriverStatus(driver.id, next);
      setDriver({ ...driver, status: next });
      showToast(next === "Available" ? "Çevrimiçisin, yeni siparişler sana atanabilir." : "Çevrimdışı oldun.", "success");
    } catch (err) {
      showToast(extractError(err, "Durum güncellenemedi."), "error");
    } finally {
      setStatusUpdating(false);
    }
  };

  const changeDelivery = async (order: IDriverActiveOrder, next: DeliveryStatusValue) => {
    setBusyDeliveryId(order.deliveryId);
    try {
      await updateDeliveryStatus(order.deliveryId, next);
      setConfirm(null);
      setHistoryKey((k) => k + 1);
      if (next === "PickedUp") showToast("Sipariş teslim alındı, iyi yolculuklar!", "success");
      else if (next === "Delivered") showToast("Teslimat tamamlandı. Eline sağlık!", "success");
      else showToast("Teslimat iptal edildi.", "info");
    } catch (err) {
      showToast(extractError(err, "İşlem tamamlanamadı."), "error");
    } finally {
      // Sunucu kurye durumunu da değiştiriyor (teslim/iptal → Müsait); her iki durumda da tazele.
      await refreshRef.current(true);
      setBusyDeliveryId(null);
    }
  };

  const handleLogout = async () => {
    // Müsait kalan bir kuryeye sipariş atanmasın diye çıkışta çevrimdışına alınır.
    if (driver && status === "Available") {
      try { await updateDriverStatus(driver.id, "Offline"); } catch { /* çıkışı engellemesin */ }
    }
    localStorage.removeItem("user");
    navigate("/DriverLogin", { replace: true });
  };

  const locationLine = (() => {
    if (!isOnline) return { warn: false, pulse: false, icon: "bi-geo-alt", text: "Çevrimdışısın · konum paylaşılmıyor" };
    switch (sharing.error) {
      case "denied":
        return { warn: true, pulse: false, icon: "bi-geo-alt-fill", text: "Konum izni verilmedi. Tarayıcı ayarlarından konuma izin ver." };
      case "unsupported":
        return { warn: true, pulse: false, icon: "bi-geo-alt-fill", text: "Bu cihaz konum paylaşımını desteklemiyor." };
      case "unavailable":
        return { warn: true, pulse: false, icon: "bi-geo-alt-fill", text: "Konum alınamıyor, GPS'in açık olduğundan emin ol." };
      case "send":
        return { warn: true, pulse: false, icon: "bi-wifi-off", text: "Konum gönderilemedi, tekrar denenecek." };
    }
    if (sharing.lastSentAt) {
      return { warn: false, pulse: true, icon: "", text: `Konumun paylaşılıyor · ${formatRelative(sharing.lastSentAt, now)}` };
    }
    return { warn: false, pulse: false, icon: "bi-crosshair", text: "Konum alınıyor..." };
  })();

  const topbar = (
    <div className="dp-topbar">
      <div className="dp-brand">
        <div className="dp-brand__logo"><i className="bi bi-bicycle"></i></div>
        <div style={{ minWidth: 0 }}>
          <div className="dp-brand__title">Kurye Paneli</div>
          <div className="dp-brand__sub">Mutfacık</div>
        </div>
      </div>
      <div className="dp-topbar__actions">
        {driver && (
          <button className="dp-icon-btn" onClick={() => refresh(false)} disabled={refreshing} aria-label="Yenile">
            <i className={`bi bi-arrow-clockwise${refreshing ? " dp-spin" : ""}`}></i>
          </button>
        )}
        <button className="dp-icon-btn dp-icon-btn--danger" onClick={handleLogout} aria-label="Çıkış yap">
          <i className="bi bi-box-arrow-right"></i>
        </button>
      </div>
    </div>
  );

  if (profileLoading) {
    return (
      <div className="dp-page">
        {topbar}
        <div className="dp-center"><div className="spinner-border" style={{ color: "#ea580c" }} role="status" /></div>
      </div>
    );
  }

  if (profileError || !driver) {
    return (
      <div className="dp-page">
        {topbar}
        <div className="dp-container">
          <div className="dp-alert dp-alert--error">
            <i className="bi bi-exclamation-circle-fill"></i>
            <span>{profileError || "Kurye profili yüklenemedi."}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dp-page">
      {topbar}

      <div className="dp-container">
        {/* Durum kartı */}
        <div className={`dp-hero${isOnline ? "" : " dp-hero--offline"}`}>
          <div className="dp-hero__row">
            <div className="dp-hero__who">
              <div className="dp-hero__avatar">{initials(driver.driverName)}</div>
              <div style={{ minWidth: 0 }}>
                <div className="dp-hero__name">{driver.driverName}</div>
                <div className="dp-hero__vehicle">
                  {driver.vehicleType && <span><i className="bi bi-bicycle me-1"></i>{driver.vehicleType}</span>}
                  {driver.vehiclePlate && <span><i className="bi bi-credit-card-2-front me-1"></i>{driver.vehiclePlate}</span>}
                </div>
              </div>
            </div>

            {onDeliveryLocked ? (
              <div className="dp-busy-chip"><i className="bi bi-bicycle"></i> Teslimatta</div>
            ) : (
              <button
                className="dp-switch"
                onClick={toggleOnline}
                disabled={statusUpdating || !isEnabled}
                aria-pressed={isOnline}
              >
                {statusUpdating ? "Güncelleniyor..." : isOnline ? "Çevrimiçi" : "Çevrimdışı"}
                <span className={`dp-switch__track${isOnline ? " is-on" : ""}`}>
                  <span className="dp-switch__knob">
                    <i className={`bi ${isOnline ? "bi-check-lg" : "bi-power"}`}></i>
                  </span>
                </span>
              </button>
            )}
          </div>

          <div className={`dp-hero__loc${locationLine.warn ? " dp-hero__loc--warn" : ""}`}>
            {locationLine.pulse ? <span className="dp-pulse-dot" /> : <i className={`bi ${locationLine.icon}`}></i>}
            <span>{locationLine.text}</span>
          </div>
        </div>

        {!isEnabled && (
          <div className="dp-alert dp-alert--warn" style={{ marginTop: 12 }}>
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>Hesabın yönetici tarafından pasife alınmış. Bu sürede sipariş alamazsın.</span>
          </div>
        )}

        {/* Sekmeler */}
        <div className="dp-tabs" role="tablist">
          <button className={`dp-tab${tab === "active" ? " is-active" : ""}`} onClick={() => setTab("active")} role="tab" aria-selected={tab === "active"}>
            <i className="bi bi-lightning-charge-fill"></i> Aktif Siparişler
            {orders.length > 0 && <span className="dp-tab__badge">{orders.length}</span>}
          </button>
          <button className={`dp-tab${tab === "history" ? " is-active" : ""}`} onClick={() => setTab("history")} role="tab" aria-selected={tab === "history"}>
            <i className="bi bi-clock-history"></i> Geçmiş
          </button>
        </div>

        {tab === "active" ? (
          ordersLoading ? (
            <div className="dp-center"><div className="spinner-border" style={{ color: "#ea580c" }} role="status" /></div>
          ) : ordersError && orders.length === 0 ? (
            <div className="dp-alert dp-alert--error">
              <i className="bi bi-exclamation-circle-fill"></i>
              <div style={{ flex: 1 }}>
                {ordersError}
                <div>
                  <button className="dp-link-btn" style={{ paddingLeft: 0 }} onClick={() => refresh(false)}>Tekrar dene</button>
                </div>
              </div>
            </div>
          ) : orders.length === 0 ? (
            isOnline ? (
              <div className="dp-empty">
                <div className="dp-empty__icon is-waiting"><i className="bi bi-hourglass-split"></i></div>
                <div className="dp-empty__title">Yeni sipariş bekleniyor</div>
                <div className="dp-empty__text">Restoran sana bir sipariş atadığında burada görünecek. Liste otomatik olarak yenilenir.</div>
              </div>
            ) : (
              <div className="dp-empty">
                <div className="dp-empty__icon is-muted"><i className="bi bi-moon-stars-fill"></i></div>
                <div className="dp-empty__title">Çevrimdışısın</div>
                <div className="dp-empty__text">Sipariş alabilmek için çevrimiçi ol.</div>
                {isEnabled && (
                  <button className="dp-btn dp-btn--primary" onClick={toggleOnline} disabled={statusUpdating}>
                    <i className="bi bi-power"></i> Çevrimiçi ol
                  </button>
                )}
              </div>
            )
          ) : (
            <>
              {ordersError && (
                <div className="dp-alert dp-alert--warn" style={{ marginBottom: 12 }}>
                  <i className="bi bi-wifi-off"></i><span>Son güncelleme başarısız oldu, tekrar denenecek.</span>
                </div>
              )}
              {orders.length >= 2 && (
                <div className="dp-route-cta">
                  <div className="dp-route-cta__icon"><i className="bi bi-signpost-split-fill"></i></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="dp-route-cta__title">{orders.length} siparişin var</div>
                    <div className="dp-route-cta__text">Hangisine önce gideceğini ve en kısa rotayı tek haritada gör.</div>
                  </div>
                  <button className="dp-btn dp-btn--primary" onClick={() => setShowRoute(true)}>
                    <i className="bi bi-map-fill"></i> Toplu rota
                  </button>
                </div>
              )}
              <div className="dp-orders">
                {orders.map((order) => (
                  <ActiveOrderCard
                    key={order.deliveryId}
                    order={order}
                    now={now}
                    busy={busyDeliveryId === order.deliveryId}
                    onPickedUp={() => changeDelivery(order, "PickedUp")}
                    onDeliver={() => setConfirm({ kind: "deliver", order })}
                    onCancel={() => setConfirm({ kind: "cancel", order })}
                    onOpenMap={setMapTarget}
                  />
                ))}
              </div>
            </>
          )
        ) : (
          <DeliveryHistory driverId={driver.id} refreshKey={historyKey} />
        )}
      </div>

      {confirm && (
        confirm.kind === "deliver" ? (
          <ConfirmSheet
            icon="bi-check2-circle"
            tone="success"
            title="Teslimatı tamamla"
            message={
              collectsAtDoor(confirm.order) ? (
                <>
                  Müşteriden <strong>{formatMoney(confirm.order.orderPrice, confirm.order.currencyDisplayText)}</strong> tahsil ettin mi?
                  {confirm.order.paymentMethod?.nameTr && <> ({confirm.order.paymentMethod.nameTr})</>}
                </>
              ) : (
                <>#{confirm.order.orderNameUniqe} numaralı siparişi müşteriye teslim ettiğini onaylıyor musun?</>
              )
            }
            confirmLabel="Evet, teslim ettim"
            busy={busyDeliveryId === confirm.order.deliveryId}
            onConfirm={() => changeDelivery(confirm.order, "Delivered")}
            onClose={() => setConfirm(null)}
          />
        ) : (
          <ConfirmSheet
            icon="bi-x-octagon-fill"
            tone="danger"
            title="Teslimatı iptal et"
            message="Bu teslimat senden alınır ve restoran siparişi başka bir kuryeye atayabilir. Bu işlem geri alınamaz."
            confirmLabel="İptal et"
            busy={busyDeliveryId === confirm.order.deliveryId}
            onConfirm={() => changeDelivery(confirm.order, "Cancelled")}
            onClose={() => setConfirm(null)}
          />
        )
      )}

      {mapTarget && (
        <DriverMapModal target={mapTarget} currentPosition={sharing.position} onClose={() => setMapTarget(null)} />
      )}

      {showRoute && orders.length > 0 && (
        <RoutePlannerModal orders={orders} currentPosition={sharing.position} onClose={() => setShowRoute(false)} />
      )}

      {toast && (
        <div className={`dp-toast dp-toast--${toast.tone}`} role="status">
          <i className={`bi ${toast.tone === "success" ? "bi-check-circle-fill" : toast.tone === "error" ? "bi-exclamation-circle-fill" : "bi-bell-fill"}`}></i>
          {toast.text}
        </div>
      )}
    </div>
  );
}

export default DriverPanel;
